import * as React from "react";
import PropTypes from 'prop-types';
import axios, { AxiosResponse } from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { PagesHelper } from "./common/PagesHelper";
import OverallPage from "./pages/OverallPage";
import ProductsPage from "./pages/ProductsPage";
import PerformanceReportsPage from "./pages/PerformanceReportsPage";
import { getHeadersConfig } from "./utils/config";
import ErrorHandler from "./utils/ErrorHandler";
import { PageName, PageState, MenuItem } from "./utils/types";
import { ResponseStatus, UserListResponseDto, UserPermissions } from "./utils/types";
import { Switch, Route, Redirect, useLocation, useHistory } from "react-router-dom";
import urlParser from "./utils/urlParser";
import CurrentUserBar from "./app/CurrentUserBar";
import { Alert } from "react-bootstrap";
import StateHelper from "./utils/StateHelper";
import CampaignLinksPage from "./pages/CampaignLinksPage";
import CampaignLookupPage from "./pages/CampaignLookupPage";
import { setDocumentTitle } from "./utils/documentTitle";
import { joinURLs } from "./utils/urlUtil";
import { checkChildElementsContainsTagAndClasses } from "./utils/misc";

function ErrorPage(){

  return (
    <Row>
      <Col xl={3}></Col>
      <Col xl={6}>
        <Alert
            title={"Unable to connect to dashboard server."}
            key="alertServerConnectionError"
            variant="danger"
            className="text-center border border-danger"
          >
            Unable to connect to the dashboard server. Please check your internet connection and try reloading this page.
            Contact support@apptap.com if the problem continues.
        </Alert>
      </Col>
      <Col xl={3}></Col>
    </Row>
  );
}

function PageContainer(props:any){
  if (!props.currentUser) {
    return null
  }

  return (
    <>
      <Row>
        <Col>
          <Switch>
            <Route
              exact
              path="/"
              render={() => {
                  return ( props.currentUser !== null ? <Redirect to={props.overallUrl} /> : <Redirect to="/" />)
              }}
            />

            <Route path={"/" + PageName.overall}>
              <OverallPage
                currentUser={props.currentUser}
              />
            </Route>

            <Route path={"/" + PageName.products}>
              <ProductsPage
                currentUser={props.currentUser}
              /> 
            </Route>

            <Route path={"/" + PageName.performanceReports}>
              <PerformanceReportsPage
                currentUser={props.currentUser}
              /> 
            </Route>

            <Route path={"/" + PageName.campaignLinks}>
              <CampaignLinksPage currentUser={props.currentUser} /> 
            </Route>

            <Route path={"/" + PageName.campaignLookup}>
              <CampaignLookupPage currentUser={props.currentUser} /> 
            </Route>

          </Switch>
        </Col>
      </Row>
    </>
  );
}

function usersMe(setCurrentUser: any, findUsersList: any, setError: any, getPartnerManagementDashboardMenu: any) {
  const NETWOR_ERROR = "NETWOR_ERROR";
  const config = getHeadersConfig(true);

  const permissions = Object.values(UserPermissions).join(",");

  const url = joinURLs("2/users/me", process.env.REACT_APP_REPORTING_API_BASE_URL);

  axios
    .get(url, {
      params: {
        checkPermissions: permissions
      },
      ...config
    })
    .then(function (response: any) {
      let canViewAs : boolean = response.data && 
        response.data.permissions && 
        response.data.permissions.includes(UserPermissions.apptapUsersList) && 
        response.data.permissions.includes(UserPermissions.apptapDashboardsViewAsAnyUser);

      if (response.data && response.data.actualUser) {
        canViewAs = response.data.actualUser.permissions && 
        response.data.actualUser.permissions.includes(UserPermissions.apptapUsersList) && 
        response.data.actualUser.permissions.includes(UserPermissions.apptapDashboardsViewAsAnyUser);
      }

      if (canViewAs) {
        findUsersList(false);
      }

      window.current_user = {
        loginEmail: response.data.email,
        ...response.data.clinks || {}
      };
      window.permissions = response.data.permissions ? response.data.permissions : [];
      const pageState: PageState = StateHelper.getPageState();
      getPartnerManagementDashboardMenu();
      setDocumentTitle(pageState.page, response.data);
      StateHelper.setTrafficSourcesDtoState(null);
      setCurrentUser(response.data);
      StateHelper.setUserState(response.data);
    })
    .catch(function (error) {
      if( ErrorHandler.isNetworkError(error) ){
        setError(NETWOR_ERROR);
        return;
      }

      const isLoggedOut = ErrorHandler.handleLoggedOutError(error);
      if ( !isLoggedOut ) {
        setError(error);
      }
    }
  );
}

function App() {
  const [error, setError] = React.useState(null);
  const [currentUser, setCurrentUser] = React.useState(null);
  const [partnerManagementDashboardMenu, setPartnerManagementDashboardMenu] = React.useState<Record<string, Record<string, Object>>>(null);
  const [viewAsUserListDto, setViewAsUserListDto] = React.useState<UserListResponseDto>(null);
  const [activeLink, setActiveLink] = React.useState<string>(PageName.overall);
  const [,setRefresh] = React.useState(0);

  const history = useHistory();
  PagesHelper.setHistory(history);
  const location = useLocation();

  function viewAsUserOnChange(selectedValues: any) {
    const pageState : PageState = StateHelper.getPageState();

    if( selectedValues.length > 0 ){
      let userId : string = selectedValues[0].userId;
      pageState.viewAs = userId;
    }
    else {
      pageState.viewAs = null;
    }

    PagesHelper.historyPushFromPageState();
    usersMe(setCurrentUser, findUsersList, setError, getPartnerManagementDashboardMenu);
  }

  function findUsersList(includeViewAsUser : boolean){
    const config = getHeadersConfig(includeViewAsUser);
    const url = joinURLs("2/users/list", process.env.REACT_APP_REPORTING_API_BASE_URL);
    axios
      .get(url, config)
      .then(function (response: any) {

        const dto : UserListResponseDto =  {
          status : ResponseStatus.OK, 
          error : null,
          userListResponse : response.data.users
        };

        setViewAsUserListDto(dto);
        StateHelper.setUserListResponseDto(dto);
      })
      .catch(function (error) {
        const isLoggedOut : boolean = ErrorHandler.handleLoggedOutError(error);
        if(! isLoggedOut){
          const dto : UserListResponseDto =  {
            status : ResponseStatus.ERROR, 
            error : null,
            userListResponse : null
          };
          setViewAsUserListDto(dto);
          StateHelper.setUserListResponseDto(dto);
        }
      });
  }

  function getPartnerManagementDashboardMenu() {
    // don't make the fetch for the PMD menu when using "view as" mode, as the PMD will return the admin
    // menu and not the menu for the user being viewed as - this needs to be fixed on PMD before we can disable
    // this check
    if ( StateHelper.getPageState().viewAs ) {
      setPartnerManagementDashboardMenu(null);
      return;
    }

    const errorHandler = (ex: any) => {
        // the menu is non-essential, so better to handle the error and let the page load
        // ignore 403 errors to the menu - this may happen if cookies get out of sync
        // but we don't want to have an infinite loop of login issues
        // const isLoggedOut : boolean = ErrorHandler.handleLoggedOutError(error);
        console.error("Unhandled exception loading the PMD menu", ex);
        setPartnerManagementDashboardMenu(null);
    };

    try {
      const config = getHeadersConfig(true);

      // needed to avoid tripping the is_json_xhr_request check on PMD
      config.headers['Accept'] = "text/html";
      config.headers['X-Requested-With'] = "Axios";

      const menuUrl = joinURLs("menu/", process.env.REACT_APP_PARTNER_MANAGEMENT_DASHBOARD_BASE_URL, window.location.href);
      axios
        .get(menuUrl, config)
        .then(function (response: AxiosResponse<any>) {
          let dom = new DOMParser().parseFromString(response.data, response.headers['content-type'].split(";")[0] as DOMParserSupportedType);
          let navbar = dom.querySelector("nav.navbar.navbar-expand-lg");
          let pmdMenu: Record<string, Record<string, MenuItem>> = {};
          let dropdowns = navbar.querySelectorAll(".dropdown");
          dropdowns.forEach( dropdown => {
            let menuText: string = dropdown.querySelector("a.nav-link.dropdown-toggle").textContent.trim();
            let menuItems = dropdown.querySelectorAll("div.dropdown-menu a.dropdown-item");

            pmdMenu[menuText] = {};
            menuItems.forEach(menuItem => {
              let menuItemText: string = menuItem.textContent.trim();
              let menuItemLink: string = menuItem.attributes.getNamedItem("href").value;
              let menuItemId: string = menuItem.attributes.getNamedItem("id") ? menuItem.attributes.getNamedItem("id").value : "";

              if (menuItemLink.startsWith("/")) {
                var absLink = new URL(menuItemLink, process.env.REACT_APP_SERVER_PARTNER_MANAGEMENT_DASHBOARD).href;
                menuItemLink = absLink;
              }

              pmdMenu[menuText][menuItemText] = {
                link: menuItemLink,
                id: menuItemId,
                admin: checkChildElementsContainsTagAndClasses(menuItem.children, "I", ["fas", "fa-lock"])
              };
            })
          })

          // this may be an empty object, meaning the fetch succeeded, but no menu items were
          // returned - which basically means the user doesn't have access to the PMD - but we
          // don't want to show that as an error message
          setPartnerManagementDashboardMenu(pmdMenu);
        }).catch(errorHandler);
    } catch ( ex ) {
      errorHandler(ex);
    }
  }

  const overallUrl = "/overall" + PagesHelper.getInitialPageUrl(PageName.overall);

  let pageContainer : any = null;
  if (error === null) {
    pageContainer = <PageContainer
                      currentUser={currentUser}
                      overallUrl={overallUrl}
                    />;
  }
  else {
    pageContainer =  <ErrorPage />;
  }

  const envBgColor = process.env.REACT_APP_ENV === "production" ? "bg-dark" : process.env.REACT_APP_ENV === "development" ? "bg-secondary" : "bg-info";

  React.useEffect(() => {
    const pageState : PageState = StateHelper.getPageState();
    const previousPage = pageState.page;
    // check history action POP to catch history reload, back and forward. Then, update page state from url
    if (history.action === "POP") {
      urlParser.parseUrl(location.pathname + location.search, setActiveLink);
      // search in case the page don't change
      if (previousPage === pageState.page) {
        const searchFunction = StateHelper.getSearchFuntion(pageState.page);
        if (searchFunction) {
          searchFunction(false);
        }
      }
      // refresh to re render CurrentUserBar component and update view as form
      setRefresh(Date.now());
    }
  }, [location, history] );

  React.useEffect(() => {
    usersMe(setCurrentUser, findUsersList, setError, getPartnerManagementDashboardMenu);
  }, []);

  return (
    <>
      <CurrentUserBar currentUser={currentUser} viewAsUserListDto={viewAsUserListDto} onChange={viewAsUserOnChange}
        activeLink={activeLink} setActiveLink={setActiveLink} envBgColor={envBgColor} partnerManagementDashboardMenu={partnerManagementDashboardMenu}
      />

      <Container fluid>
        {pageContainer}
      </Container>
    </>
  );
}

Container.propTypes = {
  currentUser: PropTypes.object,
  overallUrl: PropTypes.string,
}

export default App;
